"use client";

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";
import { Shield, Wifi, WifiOff } from "lucide-react";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { createLivePresentationSocket, type ResilienceLiveStats } from "@/lib/liveSocket";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const PIE_COLORS = ["#22c55e", "#4ade80", "#a3e635", "#facc15", "#fb923c", "#ef4444"];

function eventPageUrl(): string {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/event`;
}

export default function LivePresentation() {
  const [stats, setStats] = useState<ResilienceLiveStats | null>(null);
  const [connected, setConnected] = useState(false);
  const [showAllTime, setShowAllTime] = useState(false);
  const [openedAtIso] = useState(() => new Date().toISOString());

  const pieData = useMemo(() => {
    if (!stats?.scoreDistribution?.length) return [];
    return stats.scoreDistribution.filter((s) => s.count > 0);
  }, [stats]);

  useEffect(() => {
    const socket = createLivePresentationSocket();

    const onStats = (payload: ResilienceLiveStats) => {
      setStats(payload);
    };

    socket.on("connect", () => setConnected(true));
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", () => setConnected(false));
    socket.on("resilience-stats", onStats);
    socket.on("connect", () => {
      socket.emit("set-range", showAllTime ? { mode: "all_time" } : { mode: "since_open", since: openedAtIso });
    });

    return () => {
      socket.off("resilience-stats", onStats);
      socket.disconnect();
    };
  }, [openedAtIso, showAllTime]);

  useEffect(() => {
    // When toggling, we reconnect so the server immediately sends a fresh snapshot.
    // (Socket.IO v4 also supports emitting without reconnecting, but this keeps state simple.)
    setStats(null);
  }, [showAllTime]);

  const completionPct = stats ? Math.round(stats.completionRatio * 1000) / 10 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Shield className="h-8 w-8 text-emerald-400 shrink-0" strokeWidth={2} />
          <div className="min-w-0">
            <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight truncate">
              Resilience test — <span className="text-emerald-400">live</span>
            </h1>
            <p className="text-xs text-slate-400 hidden sm:block">
              Updates when participants start or complete the self-assessment
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm shrink-0">
          <div className="hidden md:flex items-center gap-2">
            <Switch
              id="live-range-toggle"
              checked={showAllTime}
              onCheckedChange={setShowAllTime}
              className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20"
            />
            <Label htmlFor="live-range-toggle" className="text-slate-300 select-none">
              {showAllTime ? "All-time" : "Since opened"}
            </Label>
          </div>
          {connected ? (
            <span className="flex items-center gap-1.5 text-emerald-400">
              <Wifi className="h-4 w-4" />
              Live
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-amber-400">
              <WifiOff className="h-4 w-4" />
              Connecting…
            </span>
          )}
          <Link
            to="/event"
            className="ml-2 text-slate-400 hover:text-white text-xs underline underline-offset-2"
          >
            Open test
          </Link>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
          <section className="lg:col-span-4 flex flex-col items-center lg:items-start gap-4">
            <p className="text-sm font-medium text-slate-300 text-center lg:text-left w-full">
              Scan to open the resilience test
            </p>
            <div className="rounded-2xl bg-white p-4 shadow-xl shadow-black/40">
              <QRCode value={eventPageUrl()} size={200} level="M" />
            </div>
            <p className="text-xs text-slate-500 break-all text-center lg:text-left max-w-[220px]">
              {eventPageUrl()}
            </p>
          </section>

          <section className="lg:col-span-8 space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                <p className="text-slate-400 text-sm font-medium mb-1">Tests started</p>
                <p className="text-4xl md:text-5xl font-bold tabular-nums text-white">
                  {stats?.testsStarted ?? "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                <p className="text-slate-400 text-sm font-medium mb-1">Tests completed</p>
                <p className="text-4xl md:text-5xl font-bold tabular-nums text-emerald-400">
                  {stats?.testsCompleted ?? "—"}
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur sm:col-span-1">
                <p className="text-slate-400 text-sm font-medium mb-1">Completion rate</p>
                <p className="text-4xl md:text-5xl font-bold tabular-nums text-sky-300">
                  {stats ? `${completionPct}%` : "—"}
                </p>
                <p className="text-xs text-slate-500 mt-2">Completed ÷ started</p>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 md:p-8 backdrop-blur">
              <h2 className="text-lg font-semibold text-white mb-2">Score distribution</h2>
              <p className="text-sm text-slate-400 mb-6">
                Share of completed tests by resiliency score (5-question assessment)
              </p>
              {pieData.length === 0 ? (
                <p className="text-slate-500 text-center py-16">
                  No completed tests yet — distribution will appear here.
                </p>
              ) : (
                <div className="h-[320px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="count"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={110}
                        paddingAngle={2}
                        label={({ name, percent }) =>
                          `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                        }
                      >
                        {pieData.map((_, i) => (
                          <Cell key={pieData[i].label} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "rgba(15,23,42,0.95)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: "8px",
                        }}
                        labelStyle={{ color: "#e2e8f0" }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
